// filepath: apps/web/src/renderers/form/fields/richText/sanitizeConfig.ts
//
// FR-UI-CORE-7 AC-2 — single source of truth for the rich-text
// sanitization allowlist.
//
// SECURITY INVARIANTS (binary — any drift is a regression):
//   - INV-9: never render sanitized HTML with dangerouslySetInnerHTML
//     at the renderer level. The RichText component parses the
//     sanitized DOM via DOMParser and constructs React elements
//     per-tag (no innerHTML anywhere in the SPA).
//   - FR-UI-CORE-7 AC-2: ALLOWED_TAGS / ALLOWED_ATTR / ALLOWED_URI_REGEXP
//     are the contract. Any change here must come with a unit test
//     that proves the change is non-vacuous (sanitizeConfig.test.ts).
//   - javascript: URI scheme is rejected by ALLOWED_URI_REGEXP.
//     data: and vbscript: are NOT in the regexp — they default-deny.
//   - event handler attributes (onclick / onerror / onload / etc.) are
//     NOT in ALLOWED_ATTR — DOMPurify strips them by default when
//     ALLOWED_ATTR is restricted.

import type { Config } from "dompurify";

/**
 * Allowlist of HTML tags that survive the DOMPurify pass. Anything
 * outside this list (e.g. <script>, <iframe>, <style>, <form>,
 * <object>, <embed>) is stripped before the value reaches the renderer.
 *
 * Rationale per tag:
 *   b / strong / i / em / u — basic formatting
 *   p / br — paragraph + line break
 *   ul / ol / li — lists
 *   a — links; href is allowed only when the URI matches
 *       ALLOWED_URI_REGEXP (https:, mailto:; javascript: is rejected)
 *   blockquote — quoted blocks
 *   code / pre — code blocks
 *
 * Explicitly excluded (with reason):
 *   script, style, iframe, object, embed, form, input, textarea,
 *   select, button, link, meta, base — XSS / CSRF / click-jacking surface
 */
export const ALLOWED_TAGS = [
  "b",
  "i",
  "u",
  "em",
  "strong",
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "a",
  "blockquote",
  "code",
  "pre",
] as const;

/**
 * Allowlist of HTML attributes that survive the DOMPurify pass. href
 * and title only. Style is intentionally excluded — inline styles are
 * a known XSS vector and the CSP `style-src 'self' 'unsafe-inline'`
 * covers Vite-emitted Tailwind extraction, not user content.
 *
 * Event handler attributes (onclick / onerror / onload / etc.) are
 * stripped by default when ALLOWED_ATTR is restricted.
 */
export const ALLOWED_ATTR = ["href", "title"] as const;

/**
 * URI regex for href / src attributes. Matches http(s): and mailto:
 * only. javascript:, data:, vbscript:, file: are rejected.
 *
 * The leading character class excludes control characters and
 * whitespace that could bypass a naive substring check.
 */
export const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):)/i;

/**
 * The DOMPurify config object consumed by DOMPurify.sanitize. This is
 * the SINGLE SOURCE OF TRUTH — the RichText component imports this
 * object directly. Tests assert the literal values; downstream
 * contributors must not duplicate the allowlist anywhere else.
 */
export const sanitizeConfig: Config = {
  ALLOWED_TAGS: [...ALLOWED_TAGS],
  ALLOWED_ATTR: [...ALLOWED_ATTR],
  ALLOWED_URI_REGEXP,
  // Belt and suspenders: forbid the entire HTML_HEAD profile even if a
  // future ALLOWED_TAGS edit accidentally re-adds a head-context tag.
  FORBID_TAGS: [
    "script",
    "style",
    "iframe",
    "object",
    "embed",
    "form",
    "input",
    "textarea",
    "select",
    "button",
    "link",
    "meta",
    "base",
  ],
  FORBID_ATTR: ["style", "srcdoc"],
  // KEEP_CONTENT: true preserves text nodes inside stripped tags
  // (so "Hello <script>alert(1)</script> world" becomes "Hello  world"
  // rather than "Hello  world"). DOMPurify default is true; explicit
  // here for clarity.
  KEEP_CONTENT: true,
  // No telemetry hooks, no return-dom fragment — the caller receives a
  // pure HTML string and parses it client-side.
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM: false,
};
